import { Button, ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { FormSelect } from "components/form/form-select/form-select";
import { SearchTextInput } from "components/form/inputs/text-inputs/search-text-input/search-text-input";
import { JSX, useCallback, useEffect, useState } from "react";
import { EnumUtils } from "utilities/enumerations/enum-utils";
import { NumberUtils } from "utilities/number-utils";
import { useEventSearch } from "utilities/contexts/search/event/event-search-context";
import { ToastManager } from "utilities/toast/toast-manager";
import { OrganizationService } from "utilities/services/organizations/organization-service";
import { SelectOption } from "components/form/inputs/select/select";
import { ProviderService } from "utilities/services/providers/provider-service";
import { InstructorLedTrainingType, InstructorLedTrainingTypeDisplayNames } from "models/enumerations/products/instructor-led-training-type";
import { EventType, EventTypeDisplayNames } from "models/enumerations/events/event-type";
import { PublishStatus, PublishStatusDisplayNames } from "models/enumerations/publish-status/publish-status";
import { FormCalendarDateRangePicker } from "components/form/form-calendar-date-range-picker/form-calendar-date-range-picker";
import { t } from "utilities/localization/t";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------
interface AdminEventFilterComponentProps {
    isAEN: boolean | undefined;
    products: SelectOption[];
}
// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------
const CSS_CLASS_NAME = "admin-event-filter-component";
const DEBOUNCE_TIME = 2000;
// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------
const AdminEventFilterComponent: React.FC<AdminEventFilterComponentProps> = ({isAEN, products}): JSX.Element => {
    const { initialCriteria, setCriteria } = useEventSearch();

    const [searchText, setSearchText] = useState<string>(initialCriteria?.searchText ?? "");
    const [instructorLedTrainingType, setInstructorLedTrainingType] =
        useState<InstructorLedTrainingType | undefined>(initialCriteria?.instructorLedTrainingType);
    const [eventSalesType, setEventSalesType] = useState<EventType | undefined>(
        initialCriteria?.eventSalesType
    );

    const [productId, setProductId] = useState<number | undefined>(initialCriteria?.productId);
    const [status, setStatus] = useState<PublishStatus | undefined>(initialCriteria?.status);
    const [startDate, setStartDate] = useState<Date | null>(initialCriteria?.startDate ?? null);
    const [endDate, setEndDate] = useState<Date | null>(initialCriteria?.endDate ?? null);


    const [providers, setProviders] = useState<SelectOption[]>([]);
    const { list: listProviders } = ProviderService.useList();

    const [organizations, setOrganizations] = useState<SelectOption[]>([]);
    const { list: listOrganizations } = OrganizationService.useList();
    
    const fetchProviders = useCallback(async () => {
        try {
            const listProvidersResponse = await listProviders();

            if (
                listProvidersResponse?.resultObjects == null ||
                listProvidersResponse.results == null ||
                listProvidersResponse.results.hasErrors()
            ) {
                throw new Error();
            }

            const providers = listProvidersResponse.resultObjects;
            const providerSelectOptions = providers.map((provider) => {
                return {
                    text: provider.name,
                    value: provider.id!.toString(),
                };
            }, []);


            providerSelectOptions.sort((a, b) =>
                a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            );
            providerSelectOptions.unshift({ text: "", value: "" });


            setProviders(providerSelectOptions);
        } catch {
            ToastManager.error(t("problemLoadingProviders"));
            setProviders([]);
        }
    }, [listProviders]);

    const fetchOrgs = useCallback(async () => {
        try {
            const listOrganizationsResponse = await listOrganizations();

            if (
                listOrganizationsResponse?.resultObjects == null ||
                listOrganizationsResponse.results == null ||
                listOrganizationsResponse.results.hasErrors()
            ) {
                throw new Error();
            }

            const organizations = listOrganizationsResponse.resultObjects;
            const organizationSelectOptions = organizations.map((org) => {
                return {
                    text: org.name,
                    value: org.id!.toString(),
                };
            }, []);


            organizationSelectOptions.sort((a, b) =>
                a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            );
            organizationSelectOptions.unshift({ text: "", value: "" });

            setOrganizations(organizationSelectOptions);
        } catch {
            ToastManager.error(t("problemLoadingOrganizations"));
            setProviders([]);
        }
    }, [listOrganizations]);
    
    const handleFilterByOrganizationChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        const orgValue = event.target.value === "" ? undefined : event.target.value;
        //setSelectedOrganization(orgValue);
        setCriteria((prev) => ({ ...prev, selectedOrganization: orgValue, currentPage: 1 }));
    };

    const handleFilterByProviderChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const providerValue = event.target.value === "" ? undefined : event.target.value;
        setCriteria((prev) => ({ ...prev, selectedProvider: providerValue, currentPage: 1 }));
    };

    const handleFilterByProductChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        
        const productIdIndex = NumberUtils.parseInt(event.target.value) ?? undefined;
        if (productIdIndex == null){
            setProductId(undefined);
            setCriteria((prev) => ({ ...prev, productId: undefined, currentPage: 1 }));
            return;
        }
        const newProductId = isNaN(productIdIndex) || productIdIndex < 0 ? undefined : productIdIndex;
        setProductId(newProductId);
        setCriteria((prev) => ({ ...prev, productId: newProductId, currentPage: 1 }));
    };

    const handleFilterByInstructorLedTrainingTypeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ): void => {

        if (event.target.value === "") {
            setInstructorLedTrainingType(undefined);
            setCriteria((prev) => ({ ...prev, instructorLedTrainingType: undefined, currentPage: 1 }));
            return;
        }

        const instructorLedTrainingTypeIndex = NumberUtils.parseInt(event.target.value) ?? 0;
        const newInstructorLedTrainingType =
            isNaN(instructorLedTrainingTypeIndex) || instructorLedTrainingTypeIndex < 0
                ? undefined
                : instructorLedTrainingTypeIndex;
        setInstructorLedTrainingType(newInstructorLedTrainingType);
        setCriteria((prev) => ({ ...prev, instructorLedTrainingType: newInstructorLedTrainingType, currentPage: 1 }));
    };

    const handleSearchTextChange = (searchText: string): void => {
        setSearchText(searchText);
    };

    const handleSearchTriggered = (debouncedSearchText: string): void => {
         setCriteria((prev) => ({ ...prev, 
            searchText: debouncedSearchText, 
            currentPage: 1 }));
    };

   const handleFilterByEventSalesTypeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        if (event.target.value === "") {
            setEventSalesType(undefined);
            setCriteria((prev) => ({ ...prev, eventSalesType: undefined, currentPage: 1 }));
            return;
        }

        const eventSalesTypeIndex = NumberUtils.parseInt(event.target.value) ?? 0;
        const newEventSalesType =
            isNaN(eventSalesTypeIndex) || eventSalesTypeIndex < 0 ? undefined : eventSalesTypeIndex;
        setEventSalesType(newEventSalesType);
        setCriteria((prev) => ({ ...prev, eventSalesType: newEventSalesType, currentPage: 1 }));
    };

    const handleFilterByStatusChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        
        const publishactiveStatusIndex = NumberUtils.parseInt(event.target.value) ?? undefined;
        if (publishactiveStatusIndex == null){
            setStatus(undefined);
            setCriteria((prev) => ({ ...prev, status: undefined, currentPage: 1 }));
            return;
        }

        const newStatus =
            isNaN(publishactiveStatusIndex) || publishactiveStatusIndex < 0
                ? undefined
                : publishactiveStatusIndex;
        setStatus(newStatus);
        setCriteria((prev) => ({ ...prev, status: newStatus, currentPage: 1 }));
    };

    const handleEventDateRangeChange = (date?: Array<Date | null>): void => {
        if (date) {
            setStartDate(date[0]);
            setEndDate(date[1]);
            setCriteria((prev) => ({ ...prev, startDate: date[0], endDate: date[1], currentPage: 1 }));
        }
    };

    const handleClearAll = () => {
        setProductId(undefined);
        setInstructorLedTrainingType(undefined);
        setEventSalesType(undefined);
        setStatus(undefined);
        setStartDate(null);
        setEndDate(null);
        setSearchText("");
        setCriteria((prev) => ({
            ...prev,
            selectedOrganization: undefined,
            selectedProvider: undefined,
            productId: undefined,
            instructorLedTrainingType: undefined,
            eventSalesType: undefined,
            status: undefined,
            startDate: null,
            endDate: null,
            searchText: "",
            currentPage: 1,
        }));
    };
    
    useEffect(() => {
        if (!isAEN) {
            fetchProviders();
            fetchOrgs();
        }
    }, [fetchOrgs, fetchProviders, isAEN]);
    
    return (
        <div className={`${CSS_CLASS_NAME}__sidebar`}>
           <SearchTextInput
                debounce={DEBOUNCE_TIME}
                onSearchTextInputChange={handleSearchTextChange}
                onSearchTriggered={handleSearchTriggered}
                id={"eventSearch"}
                placeholder={t("searchByEventNameOrId")}
                searchTextInputValue={searchText}
            />
            {!isAEN && (
                <>
                    <FormSelect
                        ariaLabelledBy={t("filterBySalesType")}
                        formFieldName={t("filterBySalesType")}
                        id="eventFilterBySalesType"
                        label=""
                        placeholder={t("salesType")}
                        onChange={handleFilterByEventSalesTypeChange}
                        options={[
                            { text: "", value: "" },
                            ...EnumUtils.numericEnumToSelectOptions(
                                EventType,
                                EventTypeDisplayNames
                            ),
                        ]}
                        value={eventSalesType?.toString()}
                    />
                    <FormSelect
                        ariaLabelledBy={t("filterByOrganization")}
                        formFieldName={t("filterByOrganization")}
                        id="eventFilterByOrganization"
                        label=""
                        placeholder={t("contractOrganization")}
                        onChange={handleFilterByOrganizationChange}
                        options={organizations}
                        value={initialCriteria?.selectedOrganization}
                    />
                    <FormSelect
                        ariaLabelledBy={t("filterByProvider")}
                        formFieldName={t("filterByProvider")}
                        id="eventFilterByProvider"
                        label=""
                        placeholder={t("aenProvider")}
                        onChange={handleFilterByProviderChange}
                        options={providers}
                        value={initialCriteria?.selectedProvider}
                    />
                </>
            )}
            <FormSelect
                ariaLabelledBy={t("filterByStatus")}
                formFieldName={t("filterByStatus")}
                id="eventFilterByStatus"
                label=""
                placeholder={t("status")}
                onChange={handleFilterByStatusChange}
                options={[
                    { text: "", value: "" },
                    ...EnumUtils.numericEnumToSelectOptions(
                        PublishStatus,
                        PublishStatusDisplayNames
                    ),
                ]}
                value={status?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByEventType")}
                formFieldName={t("filterByEventType")}
                id="eventFilterByEvent Type"
                label=""
                placeholder={t("eventType")}
                onChange={handleFilterByInstructorLedTrainingTypeChange}
                options={[
                    { text: "", value: "" },
                    ...EnumUtils.numericEnumToSelectOptions(
                        InstructorLedTrainingType,
                        InstructorLedTrainingTypeDisplayNames
                    ),
                ]}
                value={instructorLedTrainingType?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByProduct")}
                formFieldName={t("filterByProduct")}
                id="eventFilterByProduct"
                label=""
                placeholder={t("product")}
                onChange={handleFilterByProductChange}
                options={products}
                value={productId?.toString()}
            />

            <FormCalendarDateRangePicker
                cssClassName={`${CSS_CLASS_NAME}__event-date-range`}
                endDate={endDate}
                formFieldName="filterByDateRange"
                handleEventDateRangeChange={handleEventDateRangeChange}
                label=""
                placeholder={t("dateRange")}
                startDate={startDate}
            />
        
        <Button
            onClick={handleClearAll}
            size={ButtonSize.Medium}
            style={ButtonStyle.Secondary}
            text={t("clearAllFilters")}
        />
        </div>
    );
}

export default AdminEventFilterComponent;